import React, { useState } from 'react';
import { GoogleGenAI, Type } from "@google/genai";

interface TaxCalculatorPageProps {
  language: string;
  onBack: () => void;
  onHome: () => void;
}

const translations: Record<string, any> = {
  en: {
    title: "Tax Calculator",
    cityLabel: "Enter City or Zip Code",
    salaryLabel: "Gross Annual Salary (USD)",
    filingStatusLabel: "Filing Status",
    dependentsLabel: "Dependents",
    statusSingle: "Single",
    statusMarried: "Married Filing Jointly",
    statusHead: "Head of Household",
    calculate: "Calculate",
    calculating: "Analyzing...",
    location: "Location",
    grossSalary: "Gross Annual Salary",
    takeHome: "Estimated Net Take-home",
    breakdown: "Estimated Deductions",
    federal: "Federal Income Tax",
    fica: "FICA (Social Security & Medicare)",
    state: "State Income Tax",
    local: "Local / City Tax",
    disclaimer: "Estimates are for illustrative purposes based on standard 2025 tax rules.",
    backToHub: "Back",
    recalculate: "Edit Details",
    results: "Tax Summary"
  },
  zh: {
    title: "税后收入计算器",
    cityLabel: "输入城市或邮编",
    salaryLabel: "年薪总额 (美元)",
    filingStatusLabel: "报税身份",
    dependentsLabel: "被抚养人人数",
    statusSingle: "单身",
    statusMarried: "夫妻合报",
    statusHead: "单亲/独立供养 (Head of Household)",
    calculate: "计算",
    calculating: "计算中...",
    location: "地区",
    grossSalary: "税前年薪总额",
    takeHome: "预计税后年薪",
    breakdown: "预计扣减项",
    federal: "联邦所得税",
    fica: "FICA (社保与医疗)",
    state: "州所得税",
    local: "城市 / 地方税",
    disclaimer: "估算结果基于2025年标准税收规则，仅供参考。",
    backToHub: "返回",
    recalculate: "修改信息",
    results: "税务摘要"
  }
};

const TaxCalculatorPage: React.FC<TaxCalculatorPageProps> = ({ language, onBack, onHome }) => {
  const t = translations[language] || translations.en;
  const isRTL = language === 'ar';
  
  const [view, setView] = useState<'form' | 'result'>('form');
  const [cityInput, setCityInput] = useState('');
  const [salary, setSalary] = useState('');
  const [filingStatus, setFilingStatus] = useState('single');
  const [dependents, setDependents] = useState('0');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);

  const calculateTaxes = async () => {
    if (!cityInput || !salary) return;
    setLoading(true);
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const prompt = `Act as a US Tax Professional. Calculate the 2025 annual tax breakdown for a ${filingStatus} filer in ${cityInput} with a gross salary of $${salary} and ${dependents} dependents.
        Provide the following in JSON format:
        - city: The actual city name
        - state: Full state name
        - federalTax: Amount in USD
        - ficaTax: Amount in USD (Social Security + Medicare)
        - stateTax: Amount in USD (based on state rules for ${cityInput})
        - localTax: Amount in USD (if any local income tax for ${cityInput})
        - netPay: Annual net take-home
        - insight: A one-sentence punchy insight about the tax situation in this location in ${language === 'zh' ? 'Chinese' : 'English'}.
      `;

      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          responseMimeType: 'application/json',
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              city: { type: Type.STRING },
              state: { type: Type.STRING },
              federalTax: { type: Type.NUMBER },
              ficaTax: { type: Type.NUMBER },
              stateTax: { type: Type.NUMBER },
              localTax: { type: Type.NUMBER },
              netPay: { type: Type.NUMBER },
              insight: { type: Type.STRING }
            },
            required: ["city", "state", "federalTax", "ficaTax", "stateTax", "localTax", "netPay", "insight"]
          }
        }
      });

      const data = JSON.parse(response.text || "{}");
      setResults({ ...data, inputSalary: Number(salary) });
      setView('result');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (num: number) => 
    new Intl.NumberFormat(language === 'zh' ? 'zh-CN' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(num);

  const renderHeader = () => (
    <header className="relative z-50 p-6 md:px-12 md:py-10 flex items-center justify-between bg-white/80 backdrop-blur-md sticky top-0">
      <button onClick={onHome} className="flex items-center gap-2 active:scale-95 transition-transform group">
        <span className="material-symbols-outlined text-primary text-[26px] font-bold">flight_takeoff</span>
        <span className="font-google-sans font-bold text-[19px] tracking-tight text-slate-dark group-hover:text-primary transition-colors">Settlo</span>
      </button>
      <button 
        onClick={onBack}
        className="flex items-center gap-1 px-4 py-1.5 rounded-full bg-slate-50 border border-slate-100 text-slate-soft hover:text-primary hover:border-primary/20 transition-all active:scale-95"
      >
        <span className="text-[13px] font-bold tracking-tight">{t.backToHub}</span>
        <span className={`material-symbols-outlined text-[18px] ${isRTL ? '' : 'rotate-180'}`}>arrow_forward</span>
      </button>
    </header>
  );

  if (view === 'result' && results) {
    return (
      <div className={`relative min-h-screen bg-white font-google-sans-text overflow-x-hidden ${isRTL ? 'rtl' : 'ltr'}`}>
        <style>{`
          .tabular-nums { font-variant-numeric: tabular-nums; }
          .minus-sign { margin-right: -0.1em; } /* Visual fix for minus sign */
        `}</style>
        {renderHeader()}
        
        <main className="relative z-10 max-w-2xl mx-auto px-6 pb-24 text-left animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="mb-10">
            <h1 className="text-[38px] md:text-[48px] font-google-sans font-bold text-slate-dark tracking-tight leading-tight mb-2">
              {results.city}, <span className="text-primary">{results.state}</span>
            </h1>
            <p className="text-slate-soft text-[11px] font-bold uppercase tracking-[0.25em] opacity-50">{t.results}</p>
          </div>

          <div className="bg-white border border-slate-100 rounded-[28px] overflow-hidden shadow-xl shadow-slate-200/40">
            {/* Context Summary */}
            <div className="px-8 py-10 md:px-10 md:py-10 border-b border-slate-50 bg-slate-50/30 flex justify-between items-end">
              <div className="space-y-1">
                <div className="text-[10px] text-slate-soft/70 font-bold uppercase tracking-[0.15em]">{t.grossSalary}</div>
                <div className="text-[30px] md:text-[36px] font-google-sans font-bold text-slate-dark tracking-tight tabular-nums leading-none">
                  {formatCurrency(results.inputSalary)}
                </div>
              </div>
              <button 
                onClick={() => setView('form')}
                className="mb-0.5 px-4 py-1.5 rounded-full border border-slate-200 text-slate-soft text-[12px] font-bold hover:bg-white hover:text-primary hover:border-primary/30 transition-all active:scale-95"
              >
                {t.recalculate}
              </button>
            </div>

            {/* List Breakdown */}
            <div className="px-8 py-10 md:px-10 md:py-12 space-y-10">
              <div className="text-[10px] text-slate-soft/40 font-bold uppercase tracking-[0.3em]">{t.breakdown}</div>
              <div className="space-y-6">
                {[
                  { label: t.federal, value: results.federalTax },
                  { label: t.state, value: results.stateTax },
                  { label: t.fica, value: results.ficaTax },
                  { label: t.local, value: results.localTax }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start justify-between group">
                    <span className="text-slate-soft font-medium text-[15px] md:text-[16px] tracking-tight pr-4 leading-snug">
                      {item.label}
                    </span>
                    <span className={`text-[16px] md:text-[18px] font-bold tabular-nums tracking-tight whitespace-nowrap shrink-0 pt-0.5 ${item.value > 0 ? 'text-slate-dark' : 'text-slate-200'}`}>
                      {item.value > 0 ? (
                        <span className="flex items-center gap-[1px]">
                          <span>−</span>
                          <span>{formatCurrency(item.value)}</span>
                        </span>
                      ) : formatCurrency(item.value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Final Net Result */}
            <div className="px-8 py-12 md:px-10 md:py-14 bg-primary/[0.02] border-t border-slate-50">
              <div className="space-y-5">
                <div className="text-[11px] text-primary font-bold uppercase tracking-[0.4em]">{t.takeHome}</div>
                <div className="text-[58px] md:text-[82px] font-google-sans font-bold text-slate-dark tracking-tighter leading-[0.9] tabular-nums">
                  {formatCurrency(results.netPay)}
                </div>
                
                <div className="pt-10 mt-10 border-t border-slate-100">
                  <div className="flex gap-4 items-start">
                    <div className="mt-1 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-primary text-[14px] font-bold">auto_awesome</span>
                    </div>
                    <p className="text-[15px] md:text-[17px] text-slate-dark font-medium leading-relaxed tracking-tight">
                      {results.insight}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <footer className="mt-14 text-center max-w-sm mx-auto">
            <p className="text-[10px] text-slate-soft/30 font-bold tracking-[0.15em] leading-relaxed uppercase">
              {t.disclaimer}
            </p>
          </footer>
        </main>
      </div>
    );
  }

  return (
    <div className={`relative min-h-screen bg-white font-google-sans-text overflow-x-hidden ${isRTL ? 'rtl' : 'ltr'}`}>
      <style>{`
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
        select { 
          -webkit-appearance: none !important; 
          -moz-appearance: none !important; 
          appearance: none !important; 
          background-image: none !important;
        }
        select::-ms-expand { display: none; }
        .tabular-nums { font-variant-numeric: tabular-nums; }
      `}</style>

      {renderHeader()}

      <main className="relative z-10 max-w-2xl mx-auto px-6 pb-24 text-left animate-in fade-in duration-300">
        <div className="mb-12">
          <h1 className="text-[38px] md:text-[48px] font-google-sans font-bold text-slate-dark tracking-tight leading-tight">{t.title}</h1>
        </div>

        <div className="space-y-6 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-slate-soft/60 text-[10px] font-bold uppercase tracking-[0.15em] pl-1">{t.cityLabel}</label>
              <input 
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-5 text-[16px] font-bold text-slate-dark focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-slate-soft/60 text-[10px] font-bold uppercase tracking-[0.15em] pl-1">{t.salaryLabel}</label>
              <input 
                type="number"
                value={salary}
                onChange={(e) => setSalary(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-5 text-[16px] font-bold text-slate-dark focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-slate-soft/60 text-[10px] font-bold uppercase tracking-[0.15em] pl-1">{t.filingStatusLabel}</label>
              <div className="relative">
                <select 
                  value={filingStatus}
                  onChange={(e) => setFilingStatus(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-5 text-[16px] font-bold text-slate-dark focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all outline-none cursor-pointer pr-12"
                >
                  <option value="single">{t.statusSingle}</option>
                  <option value="married">{t.statusMarried}</option>
                  <option value="hoh">{t.statusHead}</option>
                </select>
                <span className={`material-symbols-outlined absolute ${isRTL ? 'left-5' : 'right-5'} top-1/2 -translate-y-1/2 text-slate-soft/50 pointer-events-none`}>expand_more</span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-slate-soft/60 text-[10px] font-bold uppercase tracking-[0.15em] pl-1">{t.dependentsLabel}</label>
              <div className="relative">
                <select 
                  value={dependents}
                  onChange={(e) => setDependents(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-5 text-[16px] font-bold text-slate-dark focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all outline-none cursor-pointer pr-12"
                >
                  {[0, 1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <span className={`material-symbols-outlined absolute ${isRTL ? 'left-5' : 'right-5'} top-1/2 -translate-y-1/2 text-slate-soft/50 pointer-events-none`}>expand_more</span>
              </div>
            </div>
          </div>

          <button 
            onClick={calculateTaxes}
            disabled={loading || !cityInput || !salary}
            className="w-full py-5 bg-primary text-white font-google-sans font-bold text-[17px] rounded-2xl shadow-stripe-button disabled:opacity-50 transition-all hover:bg-primary-dark active:scale-[0.98] mt-4"
          >
            {loading ? t.calculating : t.calculate}
          </button>
        </div>
      </main>
    </div>
  );
};

export default TaxCalculatorPage;